import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Send, Eye, FileText, Users, ChevronDown, ChevronUp, AlertTriangle, Sparkles, MailWarning } from 'lucide-react';

const PER_CAND_VAR_RE = /{{\s*(first_name|last_name|match_score)\s*}}/i;

const BulkEmailModal = ({ open, onOpenChange, type, recipients, recruiterId, organizationId, onSent }) => {
  const isContract = type === 'contract';
  const title = isContract ? 'Send contract email to selected' : 'Send rejection email to selected';

  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [showRecipients, setShowRecipients] = useState(false);

  // Personalisation toggle — auto-on if body uses per-candidate variables
  const [personalize, setPersonalize] = useState(true);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewItems, setPreviewItems] = useState([]); // [{name,email,subject,body,application_id}]
  const [previewIdx, setPreviewIdx] = useState(0);

  // Recipients passed in already filtered by stage on the parent; dedupe by email
  const validRecipients = useMemo(() => {
    const seen = new Set();
    return (recipients || []).filter(r => {
      const e = (r.email || '').toLowerCase().trim();
      if (!e || seen.has(e)) return false;
      seen.add(e);
      return true;
    });
  }, [recipients]);

  // Load templates on open
  useEffect(() => {
    if (!open || !organizationId) return;
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/email-templates`, {
          params: { orgId: organizationId, type },
        });
        if (cancel) return;
        const list = r.data?.data || [];
        setTemplates(list);
        if (list[0]) {
          setSelectedId(list[0].id);
          setSubject(list[0].subject);
          setBody(list[0].body);
        }
        setPreviewMode(false);
        setPreviewItems([]);
        setPreviewIdx(0);
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load templates');
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, organizationId, type]);

  // Auto-toggle personalise when body changes (only when modal first opens with a template)
  useEffect(() => {
    if (open) setPersonalize(PER_CAND_VAR_RE.test(body));
  }, [selectedId, open]); // eslint-disable-line

  const chooseTemplate = (id) => {
    setSelectedId(id);
    const t = templates.find(x => x.id === id);
    if (t) { setSubject(t.subject); setBody(t.body); }
  };

  const insertVariable = (key) => {
    setBody(b => `${b}${b && !b.endsWith('\n') ? ' ' : ''}${key}`);
  };

  const hasPerCandidateVars = PER_CAND_VAR_RE.test(body);

  // Variables to show as chips depend on mode
  const variables = personalize
    ? [
        { key: '{{first_name}}', desc: 'Candidate first name' },
        { key: '{{last_name}}', desc: 'Candidate last name' },
        { key: '{{role}}', desc: 'Position title' },
        { key: '{{company}}', desc: 'Company name' },
        { key: '{{recruiter}}', desc: 'Your name' },
        { key: '{{match_score}}', desc: 'Match score (rejection)' },
        { key: '{{today}}', desc: "Today's date" },
      ]
    : [
        { key: '{{role}}', desc: 'Position title' },
        { key: '{{company}}', desc: 'Company name' },
        { key: '{{recruiter}}', desc: 'Your name' },
        { key: '{{today}}', desc: "Today's date" },
      ];

  const handlePreview = async () => {
    try {
      setLoading(true);
      setError(null);
      if (personalize) {
        // Server renders per-candidate via /render-batch
        const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/email-templates/render-batch`, {
          template_subject: subject,
          template_body: body,
          application_ids: validRecipients.map(r => r.application_id),
          recruiter_id: recruiterId,
        });
        if (r.data?.success) {
          setPreviewItems(r.data.data || []);
          setPreviewIdx(0);
          setPreviewMode(true);
        }
      } else {
        // BCC blast — render once with org-level vars only (client-side is enough)
        const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
        const ctx = {
          today,
          role: recipients?.[0]?.role || '',
          company: recipients?.[0]?.company || '',
          recruiter: recipients?.[0]?.recruiter || '',
          first_name: '', last_name: '', match_score: '',
        };
        const render = (s) => (s || '').replace(/{{\s*(\w+)\s*}}/g, (_m, k) => (ctx[k] ?? ''));
        setPreviewItems([{ name: 'BCC blast', email: `${validRecipients.length} recipients`, subject: render(subject), body: render(body) }]);
        setPreviewIdx(0);
        setPreviewMode(true);
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to render preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (validRecipients.length === 0) return;
    try {
      setSending(true);
      setError(null);
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/email-templates/bulk-send`, {
        type,
        template_subject: subject,
        template_body: body,
        application_ids: validRecipients.map(r => r.application_id),
        recruiter_id: recruiterId,
        personalize,
      });
      if (r.data?.success || r.data?.data?.sent > 0) {
        const sentCount = r.data?.data?.sent != null ? r.data.data.sent : r.data?.data?.recipients_count || validRecipients.length;
        const failedCount = r.data?.data?.failed || 0;
        onSent?.({ type, count: sentCount, failed: failedCount, failures: r.data?.data?.failures || [] });
        onOpenChange(false);
      } else {
        setError(r.data?.message || 'Send failed');
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`bulk-email-modal-${type}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className={`w-4 h-4 ${isContract ? 'text-green-600' : 'text-gray-500'}`} />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Recipients summary */}
          <div className="bg-gray-50 border border-gray-100 rounded-md px-3 py-2">
            <button
              type="button"
              onClick={() => setShowRecipients(s => !s)}
              className="w-full flex items-center justify-between text-sm"
              data-testid="bulk-recipients-toggle"
            >
              <span className="flex items-center gap-2 text-gray-700">
                <Users className="w-4 h-4 text-gray-500" />
                <span className="font-medium">{validRecipients.length} recipient{validRecipients.length !== 1 ? 's' : ''}</span>
                {personalize ? (
                  <span className="text-gray-500">· each receives their own personalised email</span>
                ) : (
                  <span className="text-gray-500">· BCC blast — no one sees the others</span>
                )}
              </span>
              {showRecipients ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {showRecipients && (
              <div className="mt-2 pt-2 border-t border-gray-200 max-h-44 overflow-y-auto text-[12px]" data-testid="bulk-recipients-list">
                {validRecipients.map(r => (
                  <div key={r.application_id} className="flex items-center justify-between py-0.5">
                    <span className="text-gray-800">{r.name || '—'}</span>
                    <span className="text-gray-500 tabular-nums">{r.email}</span>
                  </div>
                ))}
                {validRecipients.length === 0 && (
                  <p className="text-gray-400 py-2 text-center">No valid recipient emails</p>
                )}
              </div>
            )}
          </div>

          {/* Send mode toggle */}
          {!previewMode && (
            <div className="grid grid-cols-2 gap-2" data-testid="bulk-mode-toggle">
              <button
                type="button"
                onClick={() => setPersonalize(true)}
                className={`text-left p-2.5 rounded-md border transition ${personalize ? 'border-blue-500 bg-blue-50/60 ring-1 ring-blue-200' : 'border-gray-200 hover:border-gray-300'}`}
                data-testid="bulk-mode-personalize"
              >
                <div className="flex items-center gap-1.5 text-xs font-medium text-gray-800">
                  <Sparkles className={`w-3.5 h-3.5 ${personalize ? 'text-blue-600' : 'text-gray-400'}`} />
                  Personalise each email
                </div>
                <p className="text-[11px] text-gray-500 mt-1 leading-snug">One email per recipient. Variables like <code className="font-mono text-[10px]">{`{{first_name}}`}</code> fill in.</p>
              </button>
              <button
                type="button"
                onClick={() => setPersonalize(false)}
                className={`text-left p-2.5 rounded-md border transition ${!personalize ? 'border-blue-500 bg-blue-50/60 ring-1 ring-blue-200' : 'border-gray-200 hover:border-gray-300'}`}
                data-testid="bulk-mode-bcc"
              >
                <div className="flex items-center gap-1.5 text-xs font-medium text-gray-800">
                  <MailWarning className={`w-3.5 h-3.5 ${!personalize ? 'text-blue-600' : 'text-gray-400'}`} />
                  Single BCC blast
                </div>
                <p className="text-[11px] text-gray-500 mt-1 leading-snug">One email to everyone (BCC'd). Faster, but per-candidate vars are empty.</p>
              </button>
            </div>
          )}

          {!previewMode ? (
            <>
              {/* Template picker */}
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Template</Label>
                <Select value={selectedId} onValueChange={chooseTemplate} disabled={loading}>
                  <SelectTrigger data-testid="bulk-template-select">
                    <SelectValue placeholder="Choose a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}{t.is_default ? ' (built-in)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subject */}
              <div>
                <Label htmlFor="bk-subject" className="text-xs text-gray-500 mb-1 block">Subject</Label>
                <Input
                  id="bk-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid="bulk-email-subject"
                />
              </div>

              {/* Body */}
              <div>
                <Label htmlFor="bk-body" className="text-xs text-gray-500 mb-1 block">Message</Label>
                <textarea
                  id="bk-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={12}
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="bulk-email-body"
                />
              </div>

              {/* Only show the warning in BCC mode */}
              {!personalize && hasPerCandidateVars && (
                <div className="flex items-start gap-2 text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-2 py-1.5" data-testid="bulk-vars-warning">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                  <span>
                    Per-candidate variables (<code className="font-mono">{`{{first_name}}`}</code>, <code className="font-mono">{`{{last_name}}`}</code>, <code className="font-mono">{`{{match_score}}`}</code>) will be sent as empty in a BCC blast. Switch to <strong>Personalise each email</strong> above to fill them in.
                  </span>
                </div>
              )}

              {/* Variables */}
              <div>
                <p className="text-xs text-gray-500 mb-1.5">Click to insert variable:</p>
                <div className="flex flex-wrap gap-1.5">
                  {variables.map(v => (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => insertVariable(v.key)}
                      title={v.desc}
                      className="text-[11px] font-mono bg-gray-100 hover:bg-blue-100 text-gray-700 hover:text-blue-700 px-1.5 py-0.5 rounded border border-gray-200"
                    >
                      {v.key}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            /* ─────────── PREVIEW MODE ─────────── */
            <div className="space-y-2" data-testid="bulk-email-preview">
              {personalize && previewItems.length > 1 && (
                <div className="flex items-center justify-between bg-blue-50/40 border border-blue-100 rounded-md px-3 py-2">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-gray-500">Previewing for:</span>
                    <Select value={String(previewIdx)} onValueChange={(v) => setPreviewIdx(Number(v))}>
                      <SelectTrigger className="h-7 text-xs w-auto min-w-[200px]" data-testid="bulk-preview-switcher">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {previewItems.map((p, i) => (
                          <SelectItem key={p.application_id || i} value={String(i)}>
                            {p.name || p.email || `Recipient ${i + 1}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <span className="text-[11px] text-gray-500 tabular-nums">{previewIdx + 1} / {previewItems.length}</span>
                </div>
              )}

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                  <p className="text-[10px] uppercase tracking-wide text-gray-400">To</p>
                  <p className="text-sm text-gray-700 tabular-nums">{previewItems[previewIdx]?.name} {previewItems[previewIdx]?.email && previewItems[previewIdx]?.email !== previewItems[previewIdx]?.name ? `· ${previewItems[previewIdx]?.email}` : ''}</p>
                </div>
                <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                  <p className="text-[10px] uppercase tracking-wide text-gray-400">Subject</p>
                  <p className="text-sm font-medium text-gray-900">{previewItems[previewIdx]?.subject}</p>
                </div>
                {!personalize && (
                  <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex items-center gap-2 text-[12px]">
                    <span className="text-gray-500">BCC:</span>
                    <span className="text-gray-700">{validRecipients.length} recipients</span>
                    <button onClick={() => setShowRecipients(true)} className="text-blue-600 hover:underline ml-auto" data-testid="bulk-preview-show-recipients">view all</button>
                  </div>
                )}
                <div className="px-4 py-3 bg-white whitespace-pre-wrap text-sm text-gray-800 leading-relaxed max-h-80 overflow-y-auto">
                  {previewItems[previewIdx]?.body}
                </div>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <DialogFooter className="gap-2 mt-2">
          {previewMode ? (
            <>
              <Button variant="outline" onClick={() => setPreviewMode(false)} data-testid="bulk-email-edit-back">Edit</Button>
              <Button
                onClick={handleSend}
                disabled={sending || validRecipients.length === 0}
                className={isContract ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}
                data-testid="bulk-email-send-confirm"
              >
                <Send className="w-3.5 h-3.5 mr-1.5" />
                {sending ? (personalize ? 'Sending…' : 'Sending…') : (personalize ? `Send ${validRecipients.length} personalised emails` : `Send BCC to ${validRecipients.length}`)}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending} data-testid="bulk-email-cancel">Cancel</Button>
              <Button variant="outline" onClick={handlePreview} disabled={!subject.trim() || !body.trim() || loading} data-testid="bulk-email-preview-btn">
                <Eye className="w-3.5 h-3.5 mr-1.5" />
                Preview
              </Button>
              <Button
                onClick={handleSend}
                disabled={!subject.trim() || !body.trim() || sending || validRecipients.length === 0}
                className={isContract ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}
                data-testid="bulk-email-send-btn"
              >
                <Send className="w-3.5 h-3.5 mr-1.5" />
                {sending ? 'Sending…' : (personalize ? `Send ${validRecipients.length} personalised emails` : `Send BCC to ${validRecipients.length}`)}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkEmailModal;
